import { Card, Icon, Tooltip } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Table from "examples/Tables/Table";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import NoData from "containers/DataNotAvailBox";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { RenewablesRow } from "./row";
import { useNavigate } from "react-router-dom";
import { fetchRenewables } from "services/dashboard";
import { toast } from "react-toastify";
import { dashboardTodayAllTabsSx } from "utils/dashboardTodayAllTabsSx";

export default function Renewables({ title, tooltip, uid }) {
  const [filterMode, setFilterMode] = useState("today");
  const [renewables, setRenewables] = useState([]);
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();

  const loadRenewables = useCallback(async () => {
    if (!uid) {
      return;
    }
    try {
      setLoader(true);
      const data = await fetchRenewables({ uid, filterMode });
      setRenewables(data);
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  }, [uid, filterMode]);

  useEffect(() => {
    loadRenewables();
  }, [loadRenewables]);

  const columns = [
    { name: "Client Name", align: "left", minWidth: "110px" },
    { name: "Dietitian Name", align: "left", minWidth: "120px" },
    { name: "Service Name", align: "left" },
    { name: "Start Date", align: "left" },
    { name: "Completion Date", align: "left", minWidth: "150px" },
  ];

  const rows = useMemo(
    () => RenewablesRow(renewables, navigate),
    [renewables, navigate]
  );

  const emptyMessage =
    filterMode === "today"
      ? "No renewables for today"
      : "No renewables for upgrade";

  return (
    <SuiBox mb={3} style={{ height: "100%" }}>
      <Card style={{ overflow: "inherit", height: "100%" }}>
        <SuiBox style={{ overflow: "hidden", borderRadius: 20 }}>
          <SuiBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            px={2.5}
            pt={2.5}
            gap={1}
          >
            <SuiTypography display="flex" alignItems="center">
              <SuiTypography variant="h6">{title}</SuiTypography>
              <span
                style={{
                  paddingLeft: "10px",
                  fontSize: "14px",
                  height: "16px",
                  width: "14px",
                }}
              >
                <Tooltip
                  title={tooltip}
                  placement="top"
                  style={{ color: "#67748e", height: "16px", width: "14px" }}
                >
                  <Icon color="inherit">{"help"}</Icon>
                </Tooltip>
              </span>
            </SuiTypography>
            <ToggleButtonGroup
              value={filterMode}
              exclusive
              onChange={(_, next) => {
                if (next !== null) {
                  setFilterMode(next);
                }
              }}
              size="small"
              aria-label="Renewables date filter"
              sx={dashboardTodayAllTabsSx}
            >
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          </SuiBox>

          <SuiBox
            display="flex"
            justifyContent="center"
            flexWrap="wrap"
            alignItems="center"
            style={{
              maxHeight: 185,
              minHeight: rows.length === 0 ? 185 : 0,
            }}
          >
            {!loader ? (
              <SuiBox
                id="tableBox"
                style={{
                  overflow: "scroll",
                  overflowX: "auto",
                  width: "100%",
                  borderRadius: "1rem",
                }}
                maxHeight={185}
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({
                        borders: { borderWidth, borderColor },
                      }) => `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {rows.length ? (
                  <Table columns={columns} rows={rows} />
                ) : (
                  <SuiBox
                    height={185}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <NoData message={emptyMessage} />
                  </SuiBox>
                )}
              </SuiBox>
            ) : (
              <SuiBox
                style={{ display: "flex", justifyContent: "center" }}
                mb={4}
              >
                <TailSpin color="#17c1e8" height={40} width={40} />
              </SuiBox>
            )}
          </SuiBox>
        </SuiBox>
      </Card>
    </SuiBox>
  );
}
